/**
 * Absolute document offsets, and the arithmetic for holding one section of a document
 * without losing them.
 *
 * **Every citation in Mosaic is a UTF-16 character offset into a document's whole,
 * untrimmed body.** Extracted facts, flag evidence, workspace excerpts, Ask quotes and
 * quote verification all resolve against those, and they are the offsets
 * `GET /api/filings/:accession/documents/:sequence` slices by. A reader that loads one
 * section holds a piece of the document, and an index into the piece differs from the
 * document offset by the piece's origin.
 *
 * Getting that subtraction wrong is silent: nothing throws, no offset is out of range, and
 * the reader sees a real sentence under a citation that belongs to another. So a
 * `LoadedSlice` carries its own origin, only the functions below convert between the two
 * coordinate systems, and every name says which kind it holds.
 */

/** A span of a document, in absolute offsets. Both ends index the whole body. */
export interface AbsoluteRange {
  charStart: number;
  charEnd: number;
}

/**
 * Text the reader is holding, and where it starts in the document it came from.
 *
 * `absoluteStart` is always the server's reported `charStart`, never the one the client
 * asked for: offsets are clamped rather than rejected, so a stale request can be answered
 * with a different span than it named.
 */
export interface LoadedSlice {
  absoluteStart: number;
  text: string;
}

/** One past the last character this slice holds, in absolute offsets. */
export function sliceEnd(slice: LoadedSlice): number {
  return slice.absoluteStart + slice.text.length;
}

/**
 * Whether an absolute offset falls on a character this slice holds.
 *
 * @param slice The loaded text and its origin.
 * @param absolute A document offset.
 * @returns True when the offset is at or after the slice's start and before its end.
 */
export function containsAbsolute(slice: LoadedSlice, absolute: number): boolean {
  return absolute >= slice.absoluteStart && absolute < sliceEnd(slice);
}

/**
 * An absolute offset as an index into this slice's `text`, or `null` when it falls
 * outside the slice.
 *
 * `null` rather than a clamped number: clamping would place a citation at a boundary and
 * render it as though it had been found. On `null` the caller says the passage is not in
 * view.
 *
 * The end bound is inclusive because a range's end is exclusive: a quote finishing at the
 * last character of the slice has `charEnd === sliceEnd`, a legal argument to `slice()`.
 */
export function toRelative(slice: LoadedSlice, absolute: number): number | null {
  if (!Number.isInteger(absolute)) return null;
  const relative = absolute - slice.absoluteStart;
  if (relative < 0 || relative > slice.text.length) return null;
  return relative;
}

/** An index into this slice's `text` as an absolute document offset. */
export function toAbsolute(slice: LoadedSlice, relative: number): number {
  return slice.absoluteStart + relative;
}

/** The section holding an absolute offset, or `null` if no section covers it. */
export function sectionAt<T extends AbsoluteRange>(sections: T[], absolute: number): T | null {
  return sections.find((s) => absolute >= s.charStart && absolute < s.charEnd) ?? null;
}

/**
 * The span to load so that a citation is both on screen and in its own section.
 *
 * A deep link names an absolute offset, and opening it lands on the whole section that
 * contains it: a quote read without the heading above it has an unstated subject.
 *
 * The window extends to `citationEnd` where a quote runs past the section boundary the
 * outline drew, so a heading inside a quoted passage does not cut the quote short.
 *
 * Returns `null` when no section contains the offset (an unstructured document, or an
 * offset past the end of a re-extracted one); the caller then reads the whole document.
 */
export function windowForCitation<T extends AbsoluteRange>(
  sections: T[],
  citationStart: number,
  citationEnd: number,
): AbsoluteRange | null {
  const section = sectionAt(sections, citationStart);
  if (!section) return null;
  const charEnd = Number.isInteger(citationEnd) && citationEnd > section.charEnd
    ? citationEnd
    : section.charEnd;
  return { charStart: section.charStart, charEnd };
}

/**
 * The next section after a loaded window, so a reader who reaches the bottom can carry on
 * without going back to the sidebar.
 *
 * Matched on `charStart >= windowEnd` rather than on array position, because an extended
 * window can span several sections.
 */
export function nextSection<T extends AbsoluteRange>(sections: T[], windowEnd: number): T | null {
  return sections.find((s) => s.charStart >= windowEnd) ?? null;
}

/** The section immediately before a loaded window. */
export function previousSection<T extends AbsoluteRange>(
  sections: T[],
  windowStart: number,
): T | null {
  let found: T | null = null;
  for (const s of sections) {
    if (s.charStart < windowStart) found = s;
    else break;
  }
  return found;
}
